// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCompareToggle.test.tsx
import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import { type AskPunttReview } from "@mg/schemas/src/christo/askPunttReview";
import { Columns, X } from "@phosphor-icons/react";
import { Badge, Button, Popover } from "@radix-ui/themes";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAskPunttReview } from "./useAskPunttReview";
import { comparisonBetween } from "./versionChanges";
import { type ReviewVersionOption } from "./VersionPicker";

import { useUiPrefs } from "../../utils/askPunttUiPrefs";

/** The seenTryFeatures key of the one-time compare popover. */
export const VERSION_COMPARE_TRY_FEATURE_ID = "version_compare";

/**
 * Whether the compare popover was seen. Passed `comparing`, it marks it seen
 * once compare is open, however it was opened (a chat link, View change).
 */
export function useVersionCompareSeen(comparing = false) {
  const { prefs, setPref } = useUiPrefs();
  const seen = prefs.seenTryFeatures?.[VERSION_COMPARE_TRY_FEATURE_ID] === true;
  const markSeen = useCallback(
    () =>
      setPref("seenTryFeatures", {
        ...(prefs.seenTryFeatures ?? {}),
        [VERSION_COMPARE_TRY_FEATURE_ID]: true,
      }),
    [prefs.seenTryFeatures, setPref],
  );
  useEffect(() => {
    if (comparing && !seen) markSeen();
  }, [comparing, seen, markSeen]);
  return { seen, markSeen };
}

type AskV2ReviewCompareToggleProps = {
  review: AskPunttReview;
  /** The review Compare opens beside this one: the version just before it. */
  compareReviewId: string | null;
  versionOptions: readonly ReviewVersionOption[];
  historyMessages: readonly Pick<
    AskPunttMessage,
    "comparisons" | "viewReviewId"
  >[];
  onCompare: () => void;
};

/**
 * The Compare button over the artwork, with the one-time Try popover that
 * introduces it the first time someone opens a review that has an earlier
 * version.
 */
export function AskV2ReviewCompareToggle({
  review,
  compareReviewId,
  versionOptions,
  historyMessages,
  onCompare,
}: AskV2ReviewCompareToggleProps) {
  const { t, i18n } = useTranslation("ui");
  const { seen, markSeen } = useVersionCompareSeen();
  // Closed by a click elsewhere: gone for this visit, back on the next.
  const [hidden, setHidden] = useState(false);
  // why: the header is not keyed on the review, so opening another version
  // or review keeps this instance; a new pair is a new visit.
  const visit = `${review.reviewId}:${compareReviewId ?? ""}`;
  const [hiddenVisit, setHiddenVisit] = useState(visit);
  if (hiddenVisit !== visit) {
    setHiddenVisit(visit);
    setHidden(false);
  }

  const openAt = versionOptions.findIndex(
    (option) => option.reviewId === review.reviewId,
  );
  const comparedAt = versionOptions.findIndex(
    (option) => option.reviewId === compareReviewId,
  );
  // why: options run oldest first, so this is "the open review is the newer
  // one"; a first version has nothing earlier to introduce compare with.
  const isNewer = comparedAt >= 0 && openAt > comparedAt;
  const wantsOpen = isNewer && !seen && !hidden;

  const comparedQuery = useAskPunttReview(compareReviewId, {
    enabled: wantsOpen,
  });
  const comparedReview = comparedQuery.data?.review;
  // why: the date and the areas both come from the earlier version, so
  // opening before it loads shows a stub sentence that then grows.
  const open = wantsOpen && !comparedQuery.isLoading;

  const startCompare = () => {
    markSeen();
    onCompare();
  };

  const comparedOption = versionOptions[comparedAt];
  const mark = `V${comparedOption?.versionNumber ?? comparedAt + 1}`;
  const comparison = comparisonBetween(
    historyMessages,
    review.files[0]?.name,
    comparedReview?.files[0]?.name,
    review.reviewId,
  );
  const fixed = (review.resolved ?? []).filter(
    (entry) => entry.priorReviewId === compareReviewId && !entry.reopenedAt,
  ).length;
  const areas =
    comparison != null && comparison.regions.length > 0
      ? t("askV2.review.compareTry.areasChanged", {
          count: comparison.regions.length,
        })
      : null;
  const fixes =
    fixed > 0
      ? t("askV2.review.compareTry.commentsFixed", { count: fixed })
      : null;
  const since =
    areas != null && fixes != null
      ? t("askV2.review.compareTry.sinceBoth", { areas, fixes })
      : (areas ?? fixes) != null
        ? t("askV2.review.compareTry.sinceOne", { what: areas ?? fixes })
        : null;
  const reviewedOn = comparedReview?.createdAt
    ? t("askV2.review.compareTry.reviewedOn", {
        mark,
        date: new Intl.DateTimeFormat(i18n.language, {
          month: "short",
          day: "numeric",
        }).format(new Date(comparedReview.createdAt)),
      })
    : null;
  const body = [reviewedOn, since, t("askV2.review.compareTry.sideBySide")]
    .filter((part) => part != null)
    .join(" ");

  return (
    // why: opened only by state, so a change here is a close or a click on
    // Compare, and both mark it seen.
    <Popover.Root open={open} onOpenChange={markSeen}>
      {/* why: a Trigger, not an Anchor, so closing returns focus to Compare and
          Compare names the dialog (aria-expanded / aria-controls). */}
      <Popover.Trigger>
        <Button
          variant="soft"
          color="gray"
          size="1"
          data-testid="ask-v2-review-compare-toggle"
          aria-label={t("askV2.review.compareToggleLabel")}
          onClick={startCompare}
        >
          <Columns size={14} weight="bold" aria-hidden="true" />
          {t("askV2.review.compareToggle")}
        </Button>
      </Popover.Trigger>
      <Popover.Content
        side="right"
        align="start"
        sideOffset={12}
        maxWidth="320px"
        className="max-h-[min(70vh,420px)] overflow-y-auto"
        data-testid="ask-v2-review-compare-try"
        aria-label={t("askV2.review.compareTry.label")}
        // why: it opens on its own, so a click on the artwork is not a "no
        // thanks"; only Dismiss, Esc or opening compare mark it seen.
        onInteractOutside={(event) => {
          event.preventDefault();
          setHidden(true);
        }}
      >
        <div className="flex items-center gap-2">
          <Badge
            size="1"
            variant="surface"
            color="blue"
            data-testid={`ask-puntt-try-badge-${VERSION_COMPARE_TRY_FEATURE_ID}`}
          >
            {t("tryWalkthrough.badge")}
          </Badge>
          <span className="grow text-[14px] font-semibold text-puntt-neutral-gray-12">
            {t("askV2.review.compareTry.title")}
          </span>
          <Popover.Close>
            <button
              type="button"
              className="-mr-1.5 flex size-6 items-center justify-center rounded text-puntt-neutral-gray-11 hover:bg-puntt-neutral-gray-3"
              data-testid="ask-v2-review-compare-try-dismiss"
              aria-label={t("askV2.review.compareTry.dismiss")}
            >
              <X size={14} aria-hidden="true" />
            </button>
          </Popover.Close>
        </div>
        <p
          className="mt-2 text-[13px] leading-[1.45] text-puntt-neutral-gray-11"
          data-testid="ask-v2-review-compare-try-body"
        >
          {body}
        </p>
        <div className="mt-3 flex justify-end">
          <Button
            size="1"
            data-testid="ask-v2-review-compare-try-open"
            onClick={startCompare}
          >
            {t("askV2.review.compareTry.open")}
          </Button>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}
